// Technical Staff Application Row

import ApplicationRow from '@/components/shared/ApplicationRow';
import CertificateViewModal from '@/components/sharedModals/CertificateViewModal';
import HistoryModal from '@/components/sharedModals/HistoryModal';
import ReleaseConfirmationModal from '@/components/sharedModals/ReleaseConfirmationModal';
import ApplicationViewModal from '@/components/sharedModals/applicationViewModal/ApplicationViewModal';
import useActionHotkeys from '@/hooks/useActionHotkeys';
import useEscapeKey from '@/hooks/useEscapeKey';
import { getUserRoles } from '@/utils/auth';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { ClipboardCheck, Eye, FileCheck, FileText, History, PenLine, RotateCcw, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import TS_InspectionReportsViewModal from '../../InspectionDashboardComponents/TS_InspectionReportsViewModal';
import CertificateActionHandler from './CertificateActionHandler';
import ForestRangerSignatureModal from './ForestRangerSignatureModal';
import TS_AuthenticityReviewModal from './TS_AuthenticityReviewModal';
import TS_ReviewModal from './TS_ReviewModal';

const UPDATE_PERMIT_STAGE = gql`
   mutation UpdatePermitStage(
      $id: ID!,
      $currentStage: String!,
      $status: String!,
      $notes: String,
      $approvedByTechnicalStaff: Boolean,
      $awaitingPermitCreation: Boolean,
      $acceptedByTechnicalStaff: Boolean,
      $PermitCreated: Boolean,
      $hasCertificate: Boolean,
      $certificateId: ID,
      $ApprovedInventoryRequest: Boolean,
      $awaitingOOP: Boolean,
      $certificateSignedByForestRanger: Boolean
   ) {
      updatePermitStage(
         id: $id,
         currentStage: $currentStage,
         status: $status,
         notes: $notes,
         approvedByTechnicalStaff: $approvedByTechnicalStaff,
         awaitingPermitCreation: $awaitingPermitCreation,
         acceptedByTechnicalStaff: $acceptedByTechnicalStaff,
         PermitCreated: $PermitCreated,
         hasCertificate: $hasCertificate,
         certificateId: $certificateId,
         ApprovedInventoryRequest: $ApprovedInventoryRequest,
         awaitingOOP: $awaitingOOP,
         certificateSignedByForestRanger: $certificateSignedByForestRanger
      ) {
         id
         currentStage
         status
         acceptedByTechnicalStaff
         awaitingPermitCreation
         approvedByTechnicalStaff
         PermitCreated
         hasCertificate
         certificateId
         ApprovedInventoryRequest
         awaitingOOP
         certificateSignedByForestRanger
      }
   }
   `;

const UNDO_ACCEPTANCE_TECHNICAL_STAFF = gql`
   mutation UndoAcceptanceTechnicalStaff(
      $id: ID!
      $currentStage: String!
      $status: String!
      $acceptedByTechnicalStaff: Boolean!
      $approvedByTechnicalStaff: Boolean!
      $assignedTechnicalStaffId: ID
      $assignedTechnicalStaffName: String
      $awaitingOOP: Boolean!
      $awaitingPermitCreation: Boolean!
   ) {
      undoAcceptanceTechnicalStaff(
         id: $id
         currentStage: $currentStage
         status: $status
         acceptedByTechnicalStaff: $acceptedByTechnicalStaff
         approvedByTechnicalStaff: $approvedByTechnicalStaff
         assignedTechnicalStaffId: $assignedTechnicalStaffId
         assignedTechnicalStaffName: $assignedTechnicalStaffName
         awaitingOOP: $awaitingOOP
         awaitingPermitCreation: $awaitingPermitCreation
      ) {
         id
         currentStage
         status
         recordedByReceivingClerk
         approvedByTechnicalStaff
         acceptedByTechnicalStaff
         assignedTechnicalStaffId
         assignedTechnicalStaffName
         awaitingOOP
         awaitingPermitCreation
      }
   }
   `;

const UNDO_APPROVE_INVENTORY_REQUEST = gql`
      mutation UndoApproveInventoryRequest(
         $id: ID!
         $currentStage: String!
         $status: String!
         $notes: String
         $approvedByTechnicalStaff: Boolean!
         $ApprovedInventoryRequest: Boolean!
      ) {
         undoApproveInventoryRequest(id: $id, currentStage: $currentStage, status: $status, notes: $notes, approvedByTechnicalStaff: $approvedByTechnicalStaff, ApprovedInventoryRequest: $ApprovedInventoryRequest) {
            id
            currentStage
            status
            approvedByTechnicalStaff
            ApprovedInventoryRequest
         }
      }
   `;

const GET_CERTIFICATE = gql`
   query GetCertificatesByApplicationId($applicationId: ID!) {
      getCertificatesByApplicationId(applicationId: $applicationId) {
         id
         certificateNumber
         applicationId
         applicationType
         certificateStatus
         dateCreated
         dateIssued
         expiryDate
         dateApproved
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber
         }
         csawCertificateData {
            registrationType
            ownerName
            address
            purpose
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
            otherDetails
         }
         forestRangerSignature {
            data
            contentType
         }
         orderOfPayment {
            officialReceipt {
               orNumber
               dateIssued
               amount
            }
         }
      }
   }
`;

const UPDATE_CERTIFICATE = gql`
   mutation UpdateCertificate($id: ID!, $certificateStatus: String) {
      updateCertificate(id: $id, certificateStatus: $certificateStatus) {
         id
         certificateStatus
      }
   }
   `;

const DELETE_CERTIFICATE = gql`
   mutation DeleteCertificate($id: ID!) {
      deleteCertificate(id: $id) {
         success
         message
      }
   }
`;

// Export the actions generation logic
export const getTSActions = (currentTab, app, handlers) => {
   const actions = [
      {
         icon: Eye,
         label: "View Application",
         onClick: () => handlers.setIsViewModalOpen(true),
         variant: "outline",
         className: "view-action",
         hotkey: "Ctrl + 1"
      },
      {
         icon: History,
         label: "View History",
         onClick: () => handlers.setIsHistoryModalOpen(true),
         variant: "outline",
         className: "history-action text-purple-600",
         hotkey: "Ctrl + 2"
      },
      app.status === 'Submitted' && {
         icon: ClipboardCheck,
         label: "Review Application",
         onClick: () => handlers.setIsReviewModalOpen(true),
         variant: "outline",
         className: "review-action text-blue-600"
      },
      currentTab === 'For Inspection and Approval' && {
         icon: FileCheck,
         label: "Approve Application Authenticity",
         onClick: () => handlers.setIsAuthenticityModalOpen(true),
         variant: "outline",
         className: "authenticity-action text-green-600"
      },
      (currentTab === 'Pending Forest Ranger Signature') && {
         icon: PenLine,
         label: "Sign Certificate",
         onClick: () => handlers.setIsForestRangerSignatureModalOpen(true),
         variant: "outline",
         className: "forest-ranger-signature-action text-green-600"
      },
      (currentTab === 'Awaiting Permit Creation' &&
         app.currentStage === 'AuthenticityApprovedByTechnicalStaff' &&
         !app.PermitCreated) && {
         icon: FileText,
         label: "Generate Certificate",
         onClick: () => handlers.setIsCertificateModalOpen(true),
         variant: "outline",
         className: "generate-cert-action text-green-600"
      },
      (currentTab === 'Created Permits' || currentTab === 'Pending Release' || currentTab === 'Released Certificates' || currentTab === 'Signed Certificates' || currentTab === 'Pending Forest Ranger Signature') && {
         icon: FileText,
         label: "View Certificate",
         onClick: () => handlers.setIsViewCertModalOpen(true),
         variant: "outline",
         className: "view-cert-action text-purple-600"
      },
      (currentTab === 'Approved Applications' || currentTab === 'Approved Inventory Request' || currentTab === 'Accepted Applications' || currentTab === 'Created Permits' || currentTab === 'Signed Certificates') && {
         icon: RotateCcw,
         label: `Undo ${currentTab === 'Accepted Applications' ? 'Acceptance' :
            currentTab === 'Created Permits' ? 'Permit Creation' :
               currentTab === 'Signed Certificates' ? 'Certificate Signing' :
                  'Approval'}`,
         onClick: handlers.handleUndo,
         variant: "outline",
         className: "undo-action text-yellow-600"
      }
      // (currentTab === 'Awaiting Permit Creation') && {
      //    icon: Trash2,
      //    label: "Delete Certificate",
      //    onClick: handlers.handleDeleteCertificate,
      //    variant: "outline",
      //    className: "delete-cert-action text-red-600"
      // }
   ].filter(Boolean);

   return actions;
};

const TS_ApplicationRow = ({ app, onReviewComplete, getStatusColor, currentTab, isMobile, showActionLabels = false }) => {
   const client = useApolloClient();
   const [isViewModalOpen, setIsViewModalOpen] = useState(false);
   const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
   const [isAuthenticityModalOpen, setIsAuthenticityModalOpen] = useState(false);
   const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
   const [isViewCertModalOpen, setIsViewCertModalOpen] = useState(false);
   const [isInspectionReportsModalOpen, setIsInspectionReportsModalOpen] = useState(false);
   const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
   const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
   const [isForestRangerSignatureModalOpen, setIsForestRangerSignatureModalOpen] = useState(false);
   const userRoles = getUserRoles();
   const isForestRanger = userRoles.includes('Forest_Ranger');
   const [updatePermitStage] = useMutation(UPDATE_PERMIT_STAGE);
   const [undoAcceptanceTechnicalStaff] = useMutation(UNDO_ACCEPTANCE_TECHNICAL_STAFF);
   const [undoApproveInventoryRequest] = useMutation(UNDO_APPROVE_INVENTORY_REQUEST);
   const [updateCertificate] = useMutation(UPDATE_CERTIFICATE);
   const [deleteCertificate] = useMutation(DELETE_CERTIFICATE);

   const { data: certData, loading: certLoading, error: certError } = useQuery(GET_CERTIFICATE, {
      variables: { applicationId: app.id },
      skip: !app.id
   });

   const handleReviewComplete = () => {
      setIsReviewModalOpen(false);
      onReviewComplete();
   };

   const handleUndo = async () => {
      try {
         let variables;

         if (currentTab === 'Approved Applications') {
            variables = {
               id: app.id,
               currentStage: 'ForInspectionByTechnicalStaff',
               status: 'In Progress',
               notes: 'Approval undone by Technical Staff',
               approvedByTechnicalStaff: false,
               awaitingPermitCreation: false
            };
         } else if (currentTab === 'Approved Inventory Request') {
            try {
               const { data } = await undoApproveInventoryRequest({
                  variables: {
                     id: app.id,
                     currentStage: 'ForInspectionByTechnicalStaff',
                     status: 'In Progress',
                     notes: 'Inventory request undone by Technical Staff',
                     approvedByTechnicalStaff: false,
                     ApprovedInventoryRequest: false
                  }
               });

               if (data?.undoApproveInventoryRequest) {
                  toast.success('Inventory request undone successfully');
                  onReviewComplete();
               } else {
                  toast.error('Failed to undo inventory request');
               }
            } catch (error) {
               console.error('Error undoing inventory request:', error);
               toast.error(error.message || 'Failed to undo inventory request');
            }
         } else if (currentTab === 'Accepted Applications') {
            try {
               const { data } = await undoAcceptanceTechnicalStaff({
                  variables: {
                     id: app.id,
                     currentStage: 'TechnicalStaffReview',
                     status: 'Submitted',
                     acceptedByTechnicalStaff: false,
                     approvedByTechnicalStaff: false,
                     assignedTechnicalStaffId: null,
                     assignedTechnicalStaffName: null,
                     awaitingOOP: false,
                     awaitingPermitCreation: false
                  }
               });

               if (data?.undoAcceptanceTechnicalStaff) {
                  toast.success('Application status undone successfully');
                  onReviewComplete();
               } else {
                  toast.error('Failed to undo application status');
               }
            } catch (error) {
               console.error('Error undoing acceptance:', error);
               toast.error(error.message || 'Failed to undo application status');
            }
         } else if (currentTab === 'Created Permits') {
            variables = {
               id: app.id,
               currentStage: 'AuthenticityApprovedByTechnicalStaff',
               status: 'In Progress',
               notes: 'Permit creation undone by Technical Staff',
               PermitCreated: false,
               awaitingPermitCreation: true,
               hasCertificate: false,
               certificateId: null,
               certificateSignedByPENRCENROfficer: false
            };
         } else if (currentTab === 'Signed Certificates') {
            variables = {
               id: app.id,
               currentStage: 'PendingForestRangerSignature',
               status: 'In Progress',
               certificateSignedByForestRanger: false
            };
         }

         if (variables) {
            const { data } = await updatePermitStage({ variables });
            if (data?.updatePermitStage) {
               toast.success('Application status undone successfully');
               onReviewComplete();
            }
         }
      } catch (error) {
         console.error('Error undoing application status:', error);
         toast.error(error.message || 'Failed to undo application status');
      }
   };

   const handleCertificateComplete = () => {
      setIsCertificateModalOpen(false);
      onReviewComplete();
   };

   const handleDeleteCertificate = async () => {
      try {
         // Get the latest certificate data
         const { data: certData } = await client.query({
            query: GET_CERTIFICATE,
            variables: { applicationId: app.id }
         });

         const certificate = certData?.getCertificatesByApplicationId?.[0];
         if (!certificate) {
            toast.error('No certificate found to delete');
            return;
         }

         const { data } = await deleteCertificate({
            variables: { id: certificate.id }
         });

         if (data?.deleteCertificate?.success) {
            toast.success(data.deleteCertificate.message);
            // Update permit stage
            await updatePermitStage({
               variables: {
                  id: app.id,
                  currentStage: 'AuthenticityApprovedByTechnicalStaff',
                  status: 'In Progress',
                  hasCertificate: false,
                  certificateId: null,
                  awaitingPermitCreation: true,
                  PermitCreated: false
               }
            });
            onReviewComplete();
         } else {
            toast.error('Failed to delete certificate');
         }
      } catch (error) {
         console.error('Error deleting certificate:', error);
         toast.error(error.message || 'Failed to delete certificate');
      }
   };

   const actions = getTSActions(currentTab, app, {
      handleUndo,
      setIsViewModalOpen,
      setIsReviewModalOpen,
      setIsAuthenticityModalOpen,
      setIsCertificateModalOpen,
      setIsViewCertModalOpen,
      setIsInspectionReportsModalOpen,
      setIsReleaseModalOpen,
      setIsHistoryModalOpen,
      setIsForestRangerSignatureModalOpen,
      handleDeleteCertificate
   });

   useActionHotkeys({
      actions,
      activeMainTab: currentTab,
      disabled: isViewModalOpen || isReviewModalOpen || isAuthenticityModalOpen ||
         isCertificateModalOpen || isViewCertModalOpen || isInspectionReportsModalOpen ||
         isReleaseModalOpen || isHistoryModalOpen || isForestRangerSignatureModalOpen
   });

   useEffect(() => {
      if (certError) {
         console.error('Error fetching certificate:', certError);
         toast.error('Error loading certificate');
      }
   }, [certError]);

   useEscapeKey(() => setIsViewModalOpen(false), isViewModalOpen);
   useEscapeKey(() => setIsReviewModalOpen(false), isReviewModalOpen);
   useEscapeKey(() => setIsAuthenticityModalOpen(false), isAuthenticityModalOpen);
   useEscapeKey(() => setIsCertificateModalOpen(false), isCertificateModalOpen);
   useEscapeKey(() => setIsViewCertModalOpen(false), isViewCertModalOpen);
   useEscapeKey(() => setIsInspectionReportsModalOpen(false), isInspectionReportsModalOpen);
   useEscapeKey(() => setIsReleaseModalOpen(false), isReleaseModalOpen);
   useEscapeKey(() => setIsHistoryModalOpen(false), isHistoryModalOpen);
   useEscapeKey(() => setIsForestRangerSignatureModalOpen(false), isForestRangerSignatureModalOpen);

   return (
      <>
         <ApplicationRow
            application={app}
            actions={actions}
            isMobile={isMobile}
            assigneeInfo={app.assignedTechnicalStaffName}
            showActionLabels={showActionLabels}
         />

         {/* Modals */}
         <ApplicationViewModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            application={app}
         />
         <TS_ReviewModal
            isOpen={isReviewModalOpen}
            onClose={() => setIsReviewModalOpen(false)}
            application={app}
            onReviewComplete={handleReviewComplete}
         />
         <TS_AuthenticityReviewModal
            isOpen={isAuthenticityModalOpen}
            onClose={() => setIsAuthenticityModalOpen(false)}
            application={app}
            onReviewComplete={handleReviewComplete}
         />
         <CertificateActionHandler
            isOpen={isCertificateModalOpen}
            onClose={() => setIsCertificateModalOpen(false)}
            application={app}
            onComplete={handleCertificateComplete}
         />
         {(currentTab === 'Created Permits' || currentTab === 'Pending Release' || currentTab === 'Released Certificates' || currentTab === 'Signed Certificates' || currentTab === 'Pending Forest Ranger Signature') && (
            <CertificateViewModal
               isOpen={isViewCertModalOpen}
               onClose={() => setIsViewCertModalOpen(false)}
               certificate={certData?.getCertificatesByApplicationId[0]}
               loading={certLoading}
               error={certError}
            />
         )}
         <TS_InspectionReportsViewModal
            isOpen={isInspectionReportsModalOpen}
            onClose={() => setIsInspectionReportsModalOpen(false)}
            permitId={app.id}
         />
         <ReleaseConfirmationModal
            isOpen={isReleaseModalOpen}
            onClose={() => setIsReleaseModalOpen(false)}
            application={app}
            onComplete={onReviewComplete}
         />
         <HistoryModal
            isOpen={isHistoryModalOpen}
            onClose={() => setIsHistoryModalOpen(false)}
            history={app.history}
         />
         {isForestRangerSignatureModalOpen && (
            <ForestRangerSignatureModal
               isOpen={isForestRangerSignatureModalOpen}
               onClose={() => setIsForestRangerSignatureModalOpen(false)}
               application={{
                  ...app,
                  certificateId: certData?.getCertificatesByApplicationId?.[0]?.id
               }}
               onSignComplete={onReviewComplete}
            />
         )}
      </>
   );
};

export default TS_ApplicationRow;